import React, { useMemo, useRef } from 'react'
import { motion } from 'framer-motion'
import * as R from 'remeda'
import { cubicBezier } from 'popmotion'
import { Box, Flex, Icon, Text } from '@chilipiper/design-system'
import { AlertBanner, Button, Spinner } from '@chilipiper/design-system/src/new'
import { SvgSandClock } from '@chilipiper/icons'
import { useMessageFeedContext } from '../chat-widget/MessageFeedContext'
import { useChatScroll } from '../hooks/useChatScroll'
import { useMarkLatestVisibleEvent } from '..'
import { ParsedBlock } from '../chat-data/types'
import { useConversationRoomLoader } from '../chat-widget/useConversationRoomLoader'
import { useScrollFetch } from '../hooks/useScrollFetch'
import { TranslationProvider, useTranslations } from '../context/TranslationContext'
import { Header } from './header/Header'
import { StyledAvatarImage, StyledOverflowFlex } from './styles'
import { FeedBlock } from './blocks/FeedBlock'
import { TypingAnimation } from './TypingAnimation'
import { ReconnectingStatus } from './header/ReconnectingStatus'
import { ChatInput } from './ChatInput'
import { ConsentFooter } from './ConsentFooter'
import { ExpiredSessionProvider } from './ExpiredSessionContext'
import { useAppearanceSettings } from './ChiliTheme'
import { getBlocksWithCompositeBlocks } from './compositeBlocks'

interface Props {
  onChatClose?: () => void
  onSessionRestart: (options?: { force: boolean }) => void
}

const RECONNECTING_STATES = ['RECONNECTING', 'CONNECTED', 'ERROR']

export const Chat = ({ onChatClose, onSessionRestart }: Props) => {
  const {
    blocks,
    channel,
    isTyping,
    reconnectingStatus,
    sendMessage,
    sendingMessage,
    liveChatActive,
    isLoading,
    guestId,
    roomId,
    isNarrowScreen,
    tenantId,
    workspaceId,
    piperAiActive,
    isWaitingForPiperAiResponse,
  } = useMessageFeedContext()

  const {
    isLoading: isConversationsLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationRoomLoader(roomId ?? '', true, !!roomId)
  const scrollRef = useRef<HTMLDivElement>(null)

  const { handleLastVisibleEvent } = useMarkLatestVisibleEvent(`${guestId}_guest`, blocks)
  const blocksFromLastSession = useMemo(() => {
    return R.takeLastWhile(blocks, (block, index) => {
      const isLastBlock = blocks.length - 1 === index
      return (
        // Take last block if it's conversation session expired message to show banner,
        // but only if there's no typing because that would mean new session has already started
        (isLastBlock && !isTyping) ||
        block.type !== 'SystemMessage' ||
        block.payload.type !== 'ConversationSessionExpired'
      )
    })
  }, [blocks, isTyping])

  const lastBlock = blocksFromLastSession.at(-1)
  const isSessionExpired = getIsSessionExpired(lastBlock)
  const renderableBlocks = useMemo(() => {
    return blocksFromLastSession.filter(
      block =>
        (block.type !== 'SystemMessage' ||
          block.payload.type === 'SystemConversationAssigned' ||
          block.payload.type === 'SystemConversationReassigned' ||
          block.payload.type === 'SystemConversationDeadline') &&
        block.type !== 'RouteToRep' &&
        block.type !== 'StatusChange' &&
        block.type !== 'TerminateConversation' &&
        block.type !== 'Error' &&
        block.type !== 'EnterPiperAiChat'
    )
  }, [blocksFromLastSession])

  const blocksWithCompositeBlocks = getBlocksWithCompositeBlocks(renderableBlocks, guestId ?? '')

  const { ref: feedBodyRef, isScrolledBeyondFirstMessage } = useChatScroll(renderableBlocks.length)

  const appearanceSettings = useAppearanceSettings()

  useScrollFetch({
    scrollRef,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
  })

  if (!renderableBlocks || !channel) {
    return null
  }
  const isReconnecting = RECONNECTING_STATES.includes(reconnectingStatus)
  const isInitiated = renderableBlocks.length > 2
  const isLoadingInitialMessages = isLoading || (isConversationsLoading && !isInitiated)
  const isLastBlockFillFormSubmitted =
    lastBlock?.type === 'FillForm' && !!Object.keys(lastBlock.values ?? {}).length

  return (
    <ExpiredSessionProvider value={isSessionExpired}>
      <TranslationProvider tenantId={tenantId} workspaceId={workspaceId ?? ''}>
        <Flex
          data-test-id='Chat-wrapper'
          flexDirection='column'
          boxShadow={isNarrowScreen ? undefined : 'border/popover'}
          bg='bg/section'
          w='100%'
          h='100%'
          borderRadius={isNarrowScreen ? 0 : 20}
        >
          <Box flexShrink={0}>
            <Header
              channel={channel}
              onClose={onChatClose}
              onRestart={() => onSessionRestart({ force: true })}
              isDividerFullWidth={isScrolledBeyondFirstMessage}
              showRestart={isInitiated && !liveChatActive}
            />
            {isReconnecting && <ReconnectingStatus reconnectingStatus={reconnectingStatus} />}
          </Box>
          <StyledOverflowFlex ref={scrollRef} flexDirection='column' flexGrow={1} w='100%'>
            {isConversationsLoading && !isLoadingInitialMessages && (
              <Flex mt={2} alignSelf='center'>
                <Spinner />
              </Flex>
            )}
            {!!isError && (
              <Flex>
                <RefetchingErrorAlertBanner onClick={refetch} />
              </Flex>
            )}

            <Box display='flex' flexDirection='column' w='100%' p={6} ref={feedBodyRef}>
              <Flex flexDirection='column' gap={5}>
                {blocksWithCompositeBlocks?.map(block => (
                  <FeedBlock
                    key={block.id}
                    block={block}
                    onLiveMessageVisible={handleLastVisibleEvent}
                  />
                ))}
              </Flex>
              <AvatarTypingAnimationSequence
                isLastBlockFillFormSubmitted={isLastBlockFillFormSubmitted}
                isTyping={isTyping || isLoadingInitialMessages}
              />
            </Box>
          </StyledOverflowFlex>
          <Flex flexDirection='column' flexShrink={0}>
            {isSessionExpired ? (
              <ExpiredSessionBanner onSessionRestart={onSessionRestart} />
            ) : (
              <>
                {(liveChatActive || piperAiActive || isWaitingForPiperAiResponse) && (
                  <Box mx={3}>
                    <ChatInput
                      sendMessage={sendMessage}
                      sendingMessage={sendingMessage}
                      isDisabled={isWaitingForPiperAiResponse}
                    />
                  </Box>
                )}
                <Box pt={2} pb={3} px={5}>
                  <ConsentFooter consentFooter={appearanceSettings?.consentFooter || ''} />
                </Box>
              </>
            )}
          </Flex>
        </Flex>
      </TranslationProvider>
    </ExpiredSessionProvider>
  )
}

const getIsSessionExpired = (lastBlock: ParsedBlock | undefined) => {
  return (
    lastBlock?.type === 'SystemMessage' && lastBlock.payload.type === 'ConversationSessionExpired'
  )
}

const easing = cubicBezier(0.42, 0, 0.58, 1)

export const AvatarTypingAnimationSequence = ({
  isTyping,
  isLastBlockFillFormSubmitted,
}: {
  isTyping: boolean
  isLastBlockFillFormSubmitted: boolean
}) => {
  const { channel } = useMessageFeedContext()
  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      transition={{ duration: 0.35, ease: easing }}
    >
      <Flex>
        {(isTyping || isLastBlockFillFormSubmitted) && (
          <Flex w='100%' alignItems='end' mt={5}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.15, ease: easing }}
            >
              <Box mr={3}>
                <StyledAvatarImage
                  size='40px'
                  borderRadius='10px'
                  src={channel?.avatar || undefined}
                  alt=''
                />
              </Box>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.35, duration: 0.15, ease: easing }}
            >
              <TypingAnimation />
            </motion.div>
          </Flex>
        )}
      </Flex>
    </motion.div>
  )
}

const ExpiredSessionBanner = ({ onSessionRestart }: { onSessionRestart: () => void }) => {
  const [sessionExpired, sessionExpiredDescription, startNewChat] = useTranslations([
    'sessionExpired',
    'sessionExpiredDescription',
    'startNewChat',
  ])
  return (
    <Flex flexDirection='column' m={2} p={5} gap={4} bg='bg/section-emphasis' borderRadius={12}>
      <Flex flexDirection='column' gap={3}>
        <Flex gap={2} alignItems='center'>
          <Icon color='fg/minor' icon={SvgSandClock} />
          <Text textStyle='page-section-heading' color='fg/major'>
            {sessionExpired}
          </Text>
        </Flex>
        <Text textStyle='body-multiline' color='fg/moderate'>
          {sessionExpiredDescription}
        </Text>
      </Flex>
      <Button onClick={onSessionRestart} variant='moderate' isFullWidth>
        {startNewChat ?? 'Start New Chat'}
      </Button>
    </Flex>
  )
}

const RefetchingErrorAlertBanner = ({ onClick }: { onClick: () => void }) => (
  <Box flexGrow={1} overflow='hidden' borderRadius={12} mx={6} mt={3}>
    <AlertBanner
      actions={[
        {
          onClick,
          text: 'Refetch',
          variant: 'minor',
        },
      ]}
      type='inline'
      title='Error occurred while loading messages.'
      variant='caution'
    />
  </Box>
)
